function escapeIcsText(s) {
    if (s == null) return '';
    return String(s)
        .replace(/\\/g, '\\\\')
        .replace(/\n/g, '\\n')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,');
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

function toIcsUtc(dt) {
    if (!dt || Number.isNaN(dt.getTime())) return null;
    return (
        dt.getUTCFullYear() +
        pad2(dt.getUTCMonth() + 1) +
        pad2(dt.getUTCDate()) +
        'T' +
        pad2(dt.getUTCHours()) +
        pad2(dt.getUTCMinutes()) +
        pad2(dt.getUTCSeconds()) +
        'Z'
    );
}

/**
 * Parse une date/heure renvoyée par l’API (ISO, Laravel `Y-m-d H:i:s`, etc.).
 * Évite les échecs de `Date` sur les chaînes avec espace (Safari / cohérence).
 * @param {unknown} val
 * @returns {Date|null}
 */
export function parseFlexibleDateTime(val) {
    if (val == null || val === '') return null;
    if (val instanceof Date) {
        return Number.isNaN(val.getTime()) ? null : val;
    }
    if (typeof val === 'number' && Number.isFinite(val)) {
        const n = new Date(val);
        return Number.isNaN(n.getTime()) ? null : n;
    }
    const s = String(val).trim();
    if (!s) return null;

    let dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) return dt;

    const withT = s.replace(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2}(:\d{2})?(\.\d+)?)/, (_, datePart, timePart) => `${datePart}T${timePart}`);
    if (withT !== s) {
        dt = new Date(withT);
        if (!Number.isNaN(dt.getTime())) return dt;
    }

    const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
    if (m) {
        const [, dd, mm, yyyy, hh = '0', mi = '0'] = m;
        dt = new Date(
            `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}T${String(hh).padStart(2, '0')}:${String(mi).padStart(2, '0')}:00`,
        );
        if (!Number.isNaN(dt.getTime())) return dt;
    }

    return null;
}

/**
 * Date/heure limite des ventes : `close_sale_date` ou `cloture_vente` (ISO), sinon date + heure séparées.
 * @param {Record<string, unknown>} eventLike
 * @returns {Date|null}
 */
export function parseCloseSaleEnd(eventLike) {
    if (!eventLike) return null;
    const combined = eventLike.close_sale_date ?? eventLike.cloture_vente;
    if (combined != null && combined !== '') {
        const dt = parseFlexibleDateTime(combined);
        if (dt) return dt;
    }
    if (!eventLike.date_fin_cloture) return null;
    const rawDf = String(eventLike.date_fin_cloture).trim();
    const asFull = parseFlexibleDateTime(rawDf);
    if (asFull && /[T ]\d{1,2}:\d{2}/.test(rawDf)) {
        return asFull;
    }
    const dateOnly = rawDf.split('T')[0].split(' ')[0];
    const t =
        eventLike.heure_fin_cloture && typeof eventLike.heure_fin_cloture === 'string'
            ? eventLike.heure_fin_cloture.substring(0, 8)
            : '23:59';
    const short = t.substring(0, 5);
    return (
        parseFlexibleDateTime(`${dateOnly}T${short}:00`) ||
        parseFlexibleDateTime(`${dateOnly} ${short}:00`)
    );
}

/**
 * Pour la liste publique : si l’API n’envoie pas `close_sale_date`, le déduit des champs usuels
 * et l’ajoute sur l’objet événement (ISO) pour que `SaleStatusBadge` n’utilise que `close_sale_date`.
 * @param {Record<string, unknown>} raw
 */
export function attachCloseSaleDateForPublicList(raw) {
    if (!raw || typeof raw !== 'object') return raw;
    if (raw.close_sale_date != null && String(raw.close_sale_date).trim() !== '') return raw;

    const f = raw.features && typeof raw.features === 'object' ? raw.features : {};
    const end = parseCloseSaleEnd({
        close_sale_date: f.close_sale_date ?? f.closeSaleDate ?? null,
        cloture_vente: raw.cloture_vente ?? raw.clotureVente ?? f.cloture_vente ?? null,
        date_fin_cloture:
            raw.date_fin_cloture ?? raw.dateFinCloture ?? raw.date_cloture ?? f.date_fin_cloture ?? null,
        heure_fin_cloture:
            raw.heure_fin_cloture ?? raw.heureFinCloture ?? raw.heure_cloture ?? f.heure_fin_cloture ?? null,
    });
    if (!end || Number.isNaN(end.getTime())) return raw;
    return { ...raw, close_sale_date: end.toISOString() };
}

/**
 * Texte relatif après une clôture : « il y a 2 jours », « à l’instant », etc.
 * @param {Date} closeInstant
 */
export function formatSalesClosedAgoFr(closeInstant) {
    if (!closeInstant || Number.isNaN(closeInstant.getTime())) return '';
    const ms = Date.now() - closeInstant.getTime();
    if (ms < 0) return '';
    const secTotal = Math.floor(ms / 1000);
    if (secTotal < 45) return 'à l’instant';
    const minTotal = Math.floor(secTotal / 60);
    if (minTotal < 1) return 'il y a moins d’une minute';
    if (minTotal < 60) return minTotal === 1 ? 'il y a 1 minute' : `il y a ${minTotal} minutes`;
    const hTotal = Math.floor(minTotal / 60);
    if (hTotal < 24) return hTotal === 1 ? 'il y a 1 heure' : `il y a ${hTotal} heures`;
    const dTotal = Math.floor(hTotal / 24);
    if (dTotal < 30) return dTotal === 1 ? 'il y a 1 jour' : `il y a ${dTotal} jours`;
    const moTotal = Math.floor(dTotal / 30);
    if (moTotal < 12) return moTotal === 1 ? 'il y a environ 1 mois' : `il y a environ ${moTotal} mois`;
    return 'il y a plus d’un an';
}

/** Libellé français pour affichage simple (récap billetterie). */
export function formatCloseSaleDateFr(eventLike) {
    const d = parseCloseSaleEnd(eventLike);
    if (!d) return null;
    return d.toLocaleString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function combineDateAndTime(dateStr, timeStr) {
    if (!dateStr) return null;
    const t = timeStr && typeof timeStr === 'string' ? timeStr.substring(0, 5) : '12:00';
    const d = new Date(`${String(dateStr).split('T')[0]}T${t}:00`);
    return Number.isNaN(d.getTime()) ? null : d;
}

export function addHours(date, h) {
    const x = new Date(date.getTime());
    x.setHours(x.getHours() + h);
    return x;
}

export function buildGoogleCalendarUrl({ title, details, location, start, end }) {
    if (!start) return null;
    const fmt = (dt) => dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: title || 'Événement',
        dates: `${fmt(start)}/${fmt(end || addHours(start, 2))}`,
        details: (details || '').replace(/<[^>]+>/g, '').slice(0, 900),
        location: location || '',
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function downloadIcsEvent({ title, description, location, start, end }) {
    const startUtc = toIcsUtc(start);
    const endUtc = toIcsUtc(end || addHours(start, 2));
    if (!startUtc || !endUtc) return;
    const plainDesc = (description || '').replace(/<[^>]+>/g, '');
    const ics = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Events Billetterie//FR',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        'BEGIN:VEVENT',
        `UID:${Date.now()}-events@${window.location.hostname}`,
        `DTSTAMP:${toIcsUtc(new Date())}`,
        `DTSTART:${startUtc}`,
        `DTEND:${endUtc}`,
        `SUMMARY:${escapeIcsText(title || 'Événement')}`,
        `DESCRIPTION:${escapeIcsText(plainDesc)}`,
        `LOCATION:${escapeIcsText(location || '')}`,
        'END:VEVENT',
        'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(title || 'evenement').slice(0, 40).replace(/\s+/g, '-')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
}
